import React, { useEffect, useState } from 'react';
import { Container, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { PlayCircleOutlined } from '@ant-design/icons';
import { trainingAPI } from '../../services/apiService';
import { parseVideoUrl, fetchRutubeThumbnail } from '../../utils/videoEmbed';
import { getTranslatedError } from '../../utils/errorTranslator';
import './TrainingPage.css';

function VideoThumbnail({ video, active, thumbnailUrl, onSelect }) {
  return (
    <button
      type="button"
      className={`training-playlist__item${active ? ' training-playlist__item--active' : ''}`}
      onClick={() => onSelect(video)}
    >
      <div className="training-playlist__thumb">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <div className="training-playlist__thumb-placeholder">
            <PlayCircleOutlined />
          </div>
        )}
        {active && <span className="training-playlist__playing">Сейчас</span>}
      </div>
      <span className="training-playlist__title">{video.title}</span>
    </button>
  );
}

function MainPlayer({ video }) {
  const { embedUrl } = parseVideoUrl(video.url);

  return (
    <Card className="training-player shadow-sm">
      <Card.Body>
        <h2 className="training-player__title">{video.title}</h2>
        {embedUrl ? (
          <div className="training-player__frame" key={video.id}>
            <iframe
              src={embedUrl}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="training-player__fallback">
            <p className="text-muted mb-3">
              Это видео нельзя встроить в плеер. Откройте его на сайте.
            </p>
            <Button
              variant="outline-primary"
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Открыть на сайте
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

const TrainingPage = () => {
  const [videos, setVideos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await trainingAPI.getVideos();
        const list = data.videos || [];
        setVideos(list);
        if (list.length > 0) {
          setSelectedId(list[0].id);
        }
      } catch (err) {
        setError(getTranslatedError(err));
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  useEffect(() => {
    if (videos.length === 0) return;

    let cancelled = false;

    const loadThumbnails = async () => {
      const next = {};

      await Promise.all(
        videos.map(async (video) => {
          const meta = parseVideoUrl(video.url);
          if (meta.thumbnailUrl) {
            next[video.id] = meta.thumbnailUrl;
            return;
          }
          if (meta.platform === 'rutube' && meta.id) {
            const thumb = await fetchRutubeThumbnail(meta.id);
            if (thumb) next[video.id] = thumb;
          }
        })
      );

      if (!cancelled) {
        setThumbnails((prev) => ({ ...prev, ...next }));
      }
    };

    loadThumbnails();

    return () => {
      cancelled = true;
    };
  }, [videos]);

  const selectedVideo = videos.find((v) => v.id === selectedId) || videos[0];

  return (
    <Container fluid className="py-4 training-page">
      <div className="training-page__header mb-4">
        <h1 className="training-page__heading">
          <PlayCircleOutlined className="me-2" />
          Обучение
        </h1>
        <p className="training-page__subtitle text-muted mb-0">
          Видеоинструкции по работе с сервисом
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : videos.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <PlayCircleOutlined style={{ fontSize: 48, color: '#adb5bd' }} />
            <p className="mt-3 mb-0 text-muted">Материалы скоро появятся</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="training-layout">
          <div className="training-layout__player">
            {selectedVideo && <MainPlayer video={selectedVideo} />}
          </div>

          <aside className="training-layout__sidebar">
            <div className="training-playlist">
              <h3 className="training-playlist__heading">
                Все видео ({videos.length})
              </h3>
              <div className="training-playlist__list">
                {videos.map((video) => (
                  <VideoThumbnail
                    key={video.id}
                    video={video}
                    active={video.id === selectedVideo?.id}
                    thumbnailUrl={thumbnails[video.id]}
                    onSelect={(v) => setSelectedId(v.id)}
                  />
                ))}
              </div>
            </div>
          </aside>
        </div>
      )}
    </Container>
  );
};

export default TrainingPage;
